import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

interface Tab {
  value: string;
  label: string;
  badge?: number;
  badgeColor?: string;
}

interface ChunkyTabsProps {
  activeTab: string;
  tabs?: (Tab | null | undefined)[];
  short_tabs?: string[];
  setActiveTab: (tabId: string) => void;
}

const OVERFLOW_BTN_WIDTH = 52;

export const ChunkyTabs: React.FC<ChunkyTabsProps> = ({
  activeTab,
  tabs,
  short_tabs,
  setActiveTab,
}) => {
  const localTabs = React.useMemo<Tab[]>(
    () =>
      (short_tabs?.length ?? 0) > 0 && short_tabs
        ? short_tabs.map((value) => ({ value, label: value }) as Tab)
        : tabs?.filter((tab): tab is Tab => Boolean(tab)) || [],
    [tabs, short_tabs],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const overflowBtnRef = useRef<HTMLButtonElement>(null);
  const measureRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [visibleCount, setVisibleCount] = useState(localTabs.length);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const recalculate = useCallback(() => {
    if (!containerRef.current) return;
    const available = containerRef.current.offsetWidth;
    let used = 0;
    let count = 0;

    for (let i = 0; i < localTabs.length; i++) {
      const el = measureRefs.current[i];
      if (!el) continue;
      const w = el.offsetWidth;
      const isLast = i === localTabs.length - 1;
      const needed = used + w + (isLast ? 0 : OVERFLOW_BTN_WIDTH);
      if (needed > available) break;
      used += w;
      count = i + 1;
    }

    setVisibleCount(count === 0 ? 1 : count);
  }, [localTabs.length]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(recalculate);
    observer.observe(el);
    recalculate();
    return () => observer.disconnect();
  }, [recalculate]);

  const openOverflow = () => {
    if (overflowBtnRef.current) {
      const rect = overflowBtnRef.current.getBoundingClientRect();
      const maxWidth = 420;
      const margin = 12;
      const left = Math.min(rect.left, window.innerWidth - maxWidth - margin);
      setDropdownPos({ top: rect.bottom + 6, left });
    }
    setOverflowOpen((v) => !v);
  };

  const visibleTabs = localTabs.slice(0, visibleCount);
  const overflowTabs = localTabs.slice(visibleCount);
  const hasOverflow = overflowTabs.length > 0;
  const activeInOverflow = overflowTabs.find((t) => t.value === activeTab);

  return (
    <div className="w-full relative">
      {/* Invisible measurement layer */}
      <div className="absolute top-0 left-0 invisible pointer-events-none flex flex-row">
        {localTabs.map((tab, index) => (
          <button
            key={index}
            ref={(el) => { measureRefs.current[index] = el; }}
            className="py-2 mb-2 text-sm font-medium whitespace-nowrap px-4 border"
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div ref={containerRef} className="flex flex-row min-h-fit">
        {visibleTabs.map((tab, index) => (
          <button
            key={tab.value}
            id={tab.value}
            className={`py-2 mb-2 text-sm font-medium whitespace-nowrap transition-colors duration-200 px-4 relative
              ${index === 0 ? "rounded-l-lg" : ""}
              ${!hasOverflow && index === visibleTabs.length - 1 ? "rounded-r-lg" : ""}
              ${
                activeTab === tab.value
                  ? "text-neutral-900 bg-white shadow-md border border-neutral-200"
                  : "text-neutral-400 bg-neutral-200 shadow-inner hover:border-neutral-300 border border-transparent"
              }`}
            onClick={() => setActiveTab(tab.value)}
          >
            {tab.label}
            {tab.badge != null && tab.badge > 0 && activeTab !== tab.value && (
              <span
                className="absolute flex items-center justify-center"
                style={{
                  top: 4,
                  right: 4,
                  minWidth: 8,
                  height: 8,
                  borderRadius: 999,
                  background: tab.badgeColor || "#3B82F6",
                }}
              />
            )}
          </button>
        ))}

        {hasOverflow && (
          <button
            ref={overflowBtnRef}
            className={`py-2 mb-2 px-3 text-sm font-medium rounded-r-lg whitespace-nowrap transition-colors duration-200
              ${
                activeInOverflow
                  ? "text-neutral-900 bg-white shadow-md border border-neutral-200"
                  : "text-neutral-500 bg-neutral-200 shadow-inner border border-transparent hover:bg-neutral-300"
              }`}
            onClick={openOverflow}
          >
            {activeInOverflow ? `${activeInOverflow.label} ▾` : `+${overflowTabs.length}`}
          </button>
        )}
      </div>

      {overflowOpen && hasOverflow && createPortal(
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOverflowOpen(false)}
          />
          <div
            className="fixed z-50 bg-white border border-neutral-200 rounded-xl shadow-xl p-3"
            style={{ top: dropdownPos.top, left: dropdownPos.left, maxWidth: 420, marginRight: 12 }}
          >
            <div className="flex flex-row flex-wrap gap-2">
              {overflowTabs.map((tab) => (
                <button
                  key={tab.value}
                  className={`py-1.5 px-3 rounded-lg text-sm font-medium whitespace-nowrap transition-colors
                    ${
                      activeTab === tab.value
                        ? "bg-neutral-900 text-white"
                        : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                    }`}
                  onClick={() => {
                    setActiveTab(tab.value);
                    setOverflowOpen(false);
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
};
